import * as React from "react";
import {
  customGameGenerator,
  type GameGenerator,
} from "@/game/game-generator/game-generator";
import type { MediaConfig } from "@fapinstructor/common";
import { createFileRoute } from "@tanstack/react-router";
import { GamePlayer } from "@/components/game-player";
import { GameProcessor } from "@/game/game-processor";
import { useGameScriptDialog } from "@/hooks/use-game-script-dialog";
import { gameQueryOptions } from "@/api/fapinstructor/game/get-game";
import { startGameGenerator } from "@/game/start-game-generator";
import type { SystemEvent } from "@/game/events/system-event";
import { StartGameCard } from "@/components/start-game-card";
import { Center } from "@mantine/core";
import { accountSettingsQueryOptions } from "@/api/fapinstructor/account/get-account-settings";
import { useBeforeRouteLeave } from "@/hooks/use-before-route-leave";

export const Route = createFileRoute(
  "/_authenticated/_subscribed/play/custom-game/$gameId",
)({
  loader: async (opts) => {
    const accountSettings = await opts.context.queryClient.ensureQueryData(
      accountSettingsQueryOptions(),
    );
    const game = await opts.context.queryClient.ensureQueryData(
      gameQueryOptions({ gameId: opts.params.gameId }),
    );
    return { accountSettings, game };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { accountSettings, game: gameState } = Route.useLoaderData();
  const gameGeneratorConfig = accountSettings.gameGeneratorConfig;
  const scriptId = gameGeneratorConfig.instructor.script ?? undefined;
  const voice = gameGeneratorConfig.instructor.voice ?? undefined;

  const scriptDialogQuery = useGameScriptDialog({
    scriptId,
    voice,
  });
  const scriptDialog = scriptDialogQuery.data;

  const [game, setGame] = React.useState<{
    processor: GameProcessor;
    media: MediaConfig;
    scriptId?: string;
  }>();
  const generatorRef = React.useRef<GameGenerator>(undefined);

  useBeforeRouteLeave(() => {
    if (game) {
      generatorRef.current?.return();
      generatorRef.current = undefined;
      game.processor.destroy();
    }
  });

  if (game) {
    return (
      <GamePlayer
        processor={game.processor}
        media={game.media}
        scriptId={game.scriptId}
      />
    );
  }

  return (
    <Center h="100%" mx="lg">
      <StartGameCard
        loading={scriptDialogQuery.isLoading}
        title={gameState.name}
        description={gameState.description}
        splash={gameState.splash.photo}
        onStart={() => {
          const processor = new GameProcessor({
            dialog: scriptDialog,
            voice: gameGeneratorConfig.instructor.voice ?? undefined,
            tempo: accountSettings.tempo,
            beatPatternTempo: accountSettings.beatPatternTempo,
            edgingTempoPercent: accountSettings.edgingTempoPercent,
            chatSpeed: accountSettings.chat.speed,
          });
          const generator = customGameGenerator({
            events: gameState.events as SystemEvent[],
            processor,
          });
          generatorRef.current = generator;

          startGameGenerator(processor, generator);
          setGame({ processor, media: gameState.media, scriptId });
        }}
      />
    </Center>
  );
}
